import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useVarianceDetail, useVarianceNarratives } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type { VarianceDetailRow, VarianceNarrative } from './types'

function parseStringArray(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : []
  } catch {
    return []
  }
}

function parseIsoTime(iso: string): number {
  const time = new Date(iso).getTime()
  return Number.isFinite(time) ? time : 0
}

function latestNarrativeForWeek(
  rows: VarianceNarrative[],
  weekEnding: string,
): VarianceNarrative | undefined {
  const filtered = rows.filter(row => row.week_ending === weekEnding)
  if (!filtered.length) return undefined
  return [...filtered].sort(
    (a, b) => parseIsoTime(b.generated_at) - parseIsoTime(a.generated_at),
  )[0]
}

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

const DRIVER_CHIP_STYLES = [
  'bg-teal-50 text-teal-800 border-teal-200',
  'bg-amber-50 text-amber-800 border-amber-200',
  'bg-indigo-50 text-indigo-800 border-indigo-200',
  'bg-violet-50 text-violet-800 border-violet-200',
  'bg-emerald-50 text-emerald-800 border-emerald-200',
]

function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload: { week: string; net_variance: number } }[]
}) {
  if (!active || !payload?.[0]) return null
  const datum = payload[0].payload
  return (
    <div className="rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-lg space-y-1">
      <p className="font-semibold">{fmt.weekLabel(datum.week)}</p>
      <p>Net variance: {fmt.currency(datum.net_variance)}</p>
    </div>
  )
}

export default function VarianceIntelPage() {
  const detailQuery = useVarianceDetail()
  const narrativesQuery = useVarianceNarratives()

  const allDetail = useMemo<VarianceDetailRow[]>(
    () => detailQuery.data ?? [],
    [detailQuery.data],
  )
  const allNarratives = useMemo<VarianceNarrative[]>(
    () => narrativesQuery.data ?? [],
    [narrativesQuery.data],
  )

  const weekPills = useMemo(() => {
    const unique = Array.from(new Set(allNarratives.map(narrative => narrative.week_ending)))
    unique.sort((a, b) => parseIsoTime(b) - parseIsoTime(a))
    return unique.slice(0, 13).reverse()
  }, [allNarratives])

  const defaultWeek = weekPills[weekPills.length - 1] ?? ''
  const [selectedWeek, setSelectedWeek] = useState<string>('')

  useEffect(() => {
    if (!weekPills.length) return
    setSelectedWeek(previous => {
      if (previous && weekPills.includes(previous)) return previous
      return defaultWeek
    })
  }, [defaultWeek, weekPills])

  const selectedNarrative = useMemo(
    () =>
      selectedWeek
        ? latestNarrativeForWeek(allNarratives, selectedWeek)
        : undefined,
    [allNarratives, selectedWeek],
  )

  const keyDrivers = useMemo(
    () => parseStringArray(selectedNarrative?.key_drivers),
    [selectedNarrative],
  )
  const recommendedActions = useMemo(
    () => parseStringArray(selectedNarrative?.recommended_actions),
    [selectedNarrative],
  )

  const detailForWeek = useMemo(() => {
    if (!selectedWeek) return []
    return allDetail
      .filter(row => row.week_ending === selectedWeek)
      .slice()
      .sort((a, b) => Math.abs(b.variance_dollars) - Math.abs(a.variance_dollars))
  }, [allDetail, selectedWeek])

  const favorableCount = useMemo(
    () => detailForWeek.filter(row => row.favorable).length,
    [detailForWeek],
  )
  const unfavorableCount = detailForWeek.length - favorableCount

  const largestImpact = useMemo(() => {
    if (!detailForWeek.length) return null
    return detailForWeek.reduce((best, row) =>
      Math.abs(row.variance_dollars) > Math.abs(best.variance_dollars) ? row : best,
    detailForWeek[0])
  }, [detailForWeek])

  const trendData = useMemo(
    () =>
      weekPills.map(week => {
        const narrative = latestNarrativeForWeek(allNarratives, week)
        return { week, net_variance: narrative?.net_variance ?? 0 }
      }),
    [allNarratives, weekPills],
  )

  const netVariance = selectedNarrative?.net_variance ?? 0
  const netVarianceColor =
    netVariance > 0
      ? colors.status.green
      : netVariance < 0
        ? colors.status.red
        : colors.text.secondary

  const isLoading = detailQuery.isLoading || narrativesQuery.isLoading
  const hasError = detailQuery.error || narrativesQuery.error

  if (isLoading) {
    return <CashflowV2Loading label="Loading variance intelligence…" />
  }

  if (hasError) {
    if (looksUnconfigured(hasError)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load variance data"
        description={(hasError as Error).message}
        onRetry={() => {
          detailQuery.refetch()
          narrativesQuery.refetch()
        }}
      />
    )
  }

  return (
    <div className="space-y-8 max-w-[1400px] mx-auto">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          Variance Intelligence
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          AI-generated variance analysis and root cause identification.
        </p>
      </header>

      {!weekPills.length && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No variance narratives available yet.
        </p>
      )}

      {weekPills.length > 0 && (
        <>
          <section className="flex flex-wrap gap-2 items-center">
            {weekPills.map(week => {
              const selected = week === selectedWeek
              return (
                <button
                  key={week}
                  type="button"
                  onClick={() => setSelectedWeek(week)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                    selected
                      ? 'bg-teal-500 text-white border-teal-500 shadow-sm'
                      : 'bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  {fmt.weekLabel(week)}
                </button>
              )
            })}
          </section>

          <section className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border p-6 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-teal-500/10 text-teal-700 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-teal-200">
                  Eliza AI
                </span>
                {selectedNarrative && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedNarrative.model_used}
                    <span className="mx-1.5 text-gray-300 dark:text-gray-600">·</span>
                    Generated {new Date(selectedNarrative.generated_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {selectedNarrative ? (
              <>
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
                  {selectedNarrative.narrative}
                </p>

                {keyDrivers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {keyDrivers.map((driver, index) => (
                      <span
                        key={`${driver}-${index}`}
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                          DRIVER_CHIP_STYLES[index % DRIVER_CHIP_STYLES.length]
                        }`}
                      >
                        {driver}
                      </span>
                    ))}
                  </div>
                )}

                {recommendedActions.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                      Recommended actions
                    </h3>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                      {recommendedActions.map((action, index) => (
                        <li key={`${action}-${index}`}>{action}</li>
                      ))}
                    </ol>
                  </div>
                )}

                <p className="text-xs text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-dark-border">
                  {selectedNarrative.approved_by && selectedNarrative.approved_at ? (
                    <>
                      Approved by {selectedNarrative.approved_by} on{' '}
                      {new Date(selectedNarrative.approved_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </>
                  ) : (
                    'Pending approval'
                  )}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No AI narrative for this week.
              </p>
            )}
          </section>

          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Net variance"
              value={fmt.currency(netVariance)}
              color={netVarianceColor}
            />
            <KpiCard
              label="Favorable items"
              value={fmt.num(favorableCount)}
              subtext="Line items"
            />
            <KpiCard
              label="Unfavorable items"
              value={fmt.num(unfavorableCount)}
              subtext="Line items"
            />
            <KpiCard
              label="Largest impact"
              value={
                largestImpact
                  ? fmt.currency(Math.abs(largestImpact.variance_dollars))
                  : '—'
              }
              subtext={largestImpact ? largestImpact.line_item : undefined}
            />
          </section>

          <section className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Variance detail
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Week ending {fmt.weekLabel(selectedWeek)}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-dark-surface-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3">Line item</th>
                    <th className="px-4 py-3 text-right">Forecast</th>
                    <th className="px-4 py-3 text-right">Actual</th>
                    <th className="px-4 py-3 text-right">Variance $</th>
                    <th className="px-4 py-3 text-right">Variance %</th>
                    <th className="px-4 py-3 text-center">Favorable</th>
                    <th className="px-4 py-3">Root cause</th>
                    <th className="px-4 py-3">Entity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                  {detailForWeek.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                      >
                        No variance line items for this week.
                      </td>
                    </tr>
                  ) : (
                    detailForWeek.map(row => (
                      <tr
                        key={`${row.line_item}-${row.attributed_entity}-${row.variance_dollars}`}
                        className="hover:bg-gray-50/80 dark:hover:bg-dark-surface-2/40"
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                          {row.line_item}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-200 tabular-nums">
                          {fmt.currency(row.forecast_amount)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-200 tabular-nums">
                          {fmt.currency(row.actual_amount)}
                        </td>
                        <td
                          className={`px-4 py-2.5 text-right font-medium tabular-nums ${
                            row.variance_dollars >= 0
                              ? 'text-emerald-700'
                              : 'text-red-600'
                          }`}
                        >
                          {fmt.currency(row.variance_dollars)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-200 tabular-nums">
                          {fmt.pct(row.variance_pct)}
                        </td>
                        <td className="px-4 py-2.5 text-center text-lg">
                          {row.favorable ? (
                            <span className="text-emerald-500" title="Favorable">
                              ✓
                            </span>
                          ) : (
                            <span className="text-red-500" title="Unfavorable">
                              ✗
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200 max-w-[280px]">
                          <div className="font-medium text-gray-800 dark:text-gray-100">
                            {row.root_cause_category}
                          </div>
                          {row.root_cause_detail ? (
                            <div
                              className="text-xs text-gray-500 dark:text-gray-400 truncate"
                              title={row.root_cause_detail}
                            >
                              {row.root_cause_detail}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">
                          {row.attributed_entity}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border p-5">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Net variance trend
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Last 13 weeks (from narratives)
            </p>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={colors.chart.gridLine}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="week"
                    tickFormatter={(week: string) => fmt.weekLabel(week)}
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: colors.chart.gridLine }}
                  />
                  <YAxis
                    tickFormatter={(value: number) => fmt.compact(value)}
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    axisLine={{ stroke: colors.chart.gridLine }}
                  />
                  <Tooltip content={<TrendTooltip />} />
                  <ReferenceLine
                    y={0}
                    stroke={colors.text.muted}
                    strokeDasharray="4 4"
                  />
                  <Bar dataKey="net_variance" radius={[4, 4, 0, 0]} maxBarSize={48}>
                    {trendData.map(entry => (
                      <Cell
                        key={entry.week}
                        fill={
                          entry.net_variance >= 0
                            ? colors.status.green
                            : colors.status.red
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
